import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface WeatherCondition {
  day: string;
  temperature: string;
  condition: string;
  windSpeed: string;
  waveHeight: string;
  visibility: string;
  icon: string;
}

interface PlanYourDiveProps {
  className?: string;
}

const PlanYourDive = ({ className = '' }: PlanYourDiveProps) => {
  const currentWeather: WeatherCondition[] = [
    {
      day: "Today",
      temperature: "24°C",
      condition: "Sunny",
      windSpeed: "8 km/h",
      waveHeight: "0.5m",
      visibility: "30m+",
      icon: "SunIcon"
    },
    {
      day: "Tomorrow",
      temperature: "26°C",
      condition: "Partly Cloudy",
      windSpeed: "12 km/h",
      waveHeight: "0.8m",
      visibility: "25m",
      icon: "CloudIcon"
    },
    {
      day: "Friday",
      temperature: "23°C",
      condition: "Clear",
      windSpeed: "6 km/h",
      waveHeight: "0.3m",
      visibility: "35m+",
      icon: "SunIcon"
    }
  ];

  const seasonalInfo = [
    {
      season: "Spring (March – May)",
      temperature: "18–24°C",
      visibility: "15–25 m",
      highlights: "Calm seas, warming waters, good for beginners",
      marineLife: "Octopus, Groupers, Sea Bream, Moray Eels",
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4" aria-hidden="true"><path d="M12 22V12M12 12C12 7 8 3 3 3c0 5 4 9 9 9zM12 12c0-5 4-9 9-9-1 5-5 9-9 9z"/></svg>
      ),
    },
    {
      season: "Summer (June – August)",
      temperature: "24–28°C",
      visibility: "25–35 m",
      highlights: "Warm waters, peak season, occasional plankton blooms",
      marineLife: "Barracuda, Tuna, Damselfish, Parrotfish",
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4" aria-hidden="true"><circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"/></svg>
      ),
    },
    {
      season: "Autumn (September – November)",
      temperature: "20–26°C",
      visibility: "25–30 m",
      highlights: "Warm water, fewer crowds, very stable conditions",
      marineLife: "Rays, Seahorses, Nudibranchs, Cuttlefish",
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4" aria-hidden="true"><path d="M17 8C8 10 5.9 16.17 3.82 19.5c1.5 1 3.18 1.5 5.18 1.5 4 0 8-2 10-6 2-4 1-9-2-7z"/></svg>
      ),
    },
    {
      season: "Winter (December – February)",
      temperature: "15–18°C",
      visibility: "10–25 m",
      highlights: "Cooler water, changeable sea state, fewer divers",
      marineLife: "Groupers, Amberjack, Dusky Grouper, Conger Eels",
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4" aria-hidden="true"><line x1="12" y1="2" x2="12" y2="22"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
      ),
    },
  ];

  const quickLinks = [
    {
      title: "Browse Cyprus Dive Centres",
      description: "Find certified dive operators across all regions",
      href: "/dive-centres-directory",
      icon: "BuildingOfficeIcon",
      color: "bg-primary"
    },
    {
      title: "Diving Guide & Safety in Cyprus",
      description: "Diving seasons, equipment, regulations and emergency procedures",
      href: "/cyprus-diving-information",
      icon: "ShieldCheckIcon",
      color: "bg-success"
    },
    {
      title: "Marine Conservation in Cyprus",
      description: "Explore marine conservation in Cyprus →",
      href: "/cyprus-diving-information#conservation",
      icon: "GlobeAltIcon",
      color: "bg-secondary"
    }
  ];

  return (
    <section
      className={`py-12 sm:py-14 md:py-16 ${className}`}
      style={{
        background: '#0E1C2B',
        borderTop: '1px solid rgba(255,255,255,0.06)'
      }}
    >
      <div className="container mx-auto px-4 sm:px-6">
        {/* Section Header */}
        <div className="text-center mb-8 sm:mb-10 md:mb-12">
          <h2
            className="text-2xl sm:text-3xl md:text-4xl font-headline font-bold mb-3 sm:mb-4"
            style={{ color: '#EAF4F8' }}
          >
            Plan Your Cyprus Diving Trip
          </h2>
          <p
            className="text-sm sm:text-base md:text-lg max-w-2xl mx-auto px-2 sm:px-0"
            style={{ color: '#C8DDE6' }}
          >
            Conditions, seasons, and practical planning insights to help you time your dives properly.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 sm:gap-6 md:gap-8">
          {/* Current Weather Conditions */}
          <div className="lg:col-span-2">
            <div
              className="rounded-xl p-4 sm:p-5 md:p-6"
              style={{
                background: '#0F2234',
                border: '1px solid rgba(255,255,255,0.06)'
              }}
            >
              <div className="flex items-center mb-4">
                <div className="flex items-center space-x-2">
                  <Icon name="CloudIcon" size={22} style={{ color: '#6FD3E0' }} />
                  <h3
                    className="text-base sm:text-lg md:text-xl font-headline font-semibold"
                    style={{ color: '#EAF4F8' }}
                  >
                    Current Diving Conditions
                  </h3>
                </div>
              </div>

              {/* Notice Banner — updated wording */}
              <div
                className="rounded-lg p-3 sm:p-4 mb-4 sm:mb-6"
                style={{
                  background: '#0D2030',
                  border: '1px solid rgba(111,211,224,0.15)'
                }}
              >
                <div className="flex items-start space-x-3">
                  <Icon name="InformationCircleIcon" size={20} style={{ color: '#6FD3E0' }} className="flex-shrink-0 mt-0.5" />
                  <div>
                    <p
                      className="text-xs sm:text-sm font-medium mb-1"
                      style={{ color: '#6FD3E0' }}
                    >
                      Live conditions integration coming soon.
                    </p>
                    <p
                      className="text-xs"
                      style={{ color: '#C8DDE6' }}
                    >
                      Always check current conditions with your dive centre before diving.
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-4 sm:mb-6">
                {currentWeather.map((weather) => (
                  <div
                    key={weather.day}
                    className="rounded-lg p-3 sm:p-4"
                    style={{
                      background: '#0D1E2D',
                      border: '1px solid rgba(255,255,255,0.08)'
                    }}
                  >
                    <div className="flex items-center justify-between mb-2 sm:mb-3">
                      <span className="font-medium text-sm sm:text-base" style={{ color: '#EAF4F8' }}>{weather.day}</span>
                      <Icon name={weather.icon as any} size={20} style={{ color: '#FF8A3D' }} />
                    </div>
                    <div className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm">
                      <div className="flex justify-between">
                        <span style={{ color: '#C8DDE6' }}>Temperature:</span>
                        <span className="font-medium" style={{ color: '#EAF4F8' }}>{weather.temperature}</span>
                      </div>
                      <div className="flex justify-between">
                        <span style={{ color: '#C8DDE6' }}>Wind:</span>
                        <span className="font-medium" style={{ color: '#EAF4F8' }}>{weather.windSpeed}</span>
                      </div>
                      <div className="flex justify-between">
                        <span style={{ color: '#C8DDE6' }}>Waves:</span>
                        <span className="font-medium" style={{ color: '#EAF4F8' }}>{weather.waveHeight}</span>
                      </div>
                      <div className="flex justify-between">
                        <span style={{ color: '#C8DDE6' }}>Visibility:</span>
                        <span className="font-medium" style={{ color: '#6FD3E0' }}>{weather.visibility}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Seasonal Information */}
              <div
                className="pt-6"
                style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}
              >
                <h4
                  className="font-headline font-semibold mb-4"
                  style={{ color: '#EAF4F8' }}
                >
                  Seasonal Diving Guide
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {seasonalInfo.map((season) => (
                    <div
                      key={season.season}
                      className="flex items-start space-x-3 p-3 rounded-lg transition-colors"
                      style={{ background: '#0D1E2D' }}
                    >
                      <div
                        className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-1"
                        style={{ background: 'rgba(111,211,224,0.10)', color: '#6FD3E0', border: '1px solid rgba(111,211,224,0.2)' }}
                      >
                        {season.icon}
                      </div>
                      <div>
                        <h5 className="font-medium mb-1" style={{ color: '#EAF4F8' }}>{season.season}</h5>
                        <p className="text-sm mb-1" style={{ color: '#C8DDE6' }}>
                          {season.temperature} &bull; Visibility: {season.visibility}
                        </p>
                        <p className="text-xs mb-1" style={{ color: '#C8DDE6' }}>{season.highlights}</p>
                        <p className="text-xs" style={{ color: '#C8DDE6' }}>
                          <span className="font-medium" style={{ color: '#6FD3E0' }}>Marine Life:</span> {season.marineLife}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div className="space-y-4">
            <h3
              className="text-xl font-headline font-semibold mb-4"
              style={{ color: '#EAF4F8' }}
            >
              Essential Resources
            </h3>
            {quickLinks.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                className="block rounded-xl p-4 transition-all duration-300"
                style={{
                  background: '#0F2234',
                  border: '1px solid rgba(255,255,255,0.06)'
                }}
                onMouseEnter={(e) => {
                  const el = e.currentTarget as HTMLElement;
                  el.style.border = '1px solid rgba(111,211,224,0.25)';
                  el.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  const el = e.currentTarget as HTMLElement;
                  el.style.border = '1px solid rgba(255,255,255,0.06)';
                  el.style.transform = 'translateY(0)';
                }}
              >
                <div className="flex items-start space-x-3">
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ background: 'rgba(255,138,61,0.12)', border: '1px solid rgba(255,138,61,0.25)' }}
                  >
                    <Icon name={link.icon as any} size={20} style={{ color: '#FF8A3D' }} />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-medium mb-1" style={{ color: '#EAF4F8' }}>{link.title}</h4>
                    <p className="text-sm" style={{ color: '#C8DDE6' }}>{link.description}</p>
                  </div>
                  <Icon name="ArrowRightIcon" size={16} style={{ color: '#6FD3E0' }} className="mt-1" />
                </div>
              </Link>
            ))}

            {/* Emergency Contact */}
            <div
              className="rounded-xl p-5 mt-6"
              style={{
                background: 'rgba(239,68,68,0.04)',
                border: '1px solid rgba(239,68,68,0.18)'
              }}
            >
              {/* Safety Information label */}
              <p
                className="text-xs font-semibold uppercase tracking-widest mb-3"
                style={{ color: 'rgba(200,221,230,0.55)' }}
              >
                Safety Information
              </p>
              <div className="flex items-center space-x-2 mb-3">
                <Icon name="ExclamationTriangleIcon" size={22} className="text-error" />
                <h4 className="font-headline font-bold text-error text-base">Emergency Contact</h4>
              </div>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Icon name="PhoneIcon" size={16} className="text-error" />
                  <p className="text-sm" style={{ color: '#EAF4F8' }}>
                    <strong className="text-error">Cyprus Emergency Services: 112</strong>
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <div
                    className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.25)' }}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="#f87171" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5" aria-hidden="true"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>
                  </div>
                  <p className="text-xs" style={{ color: '#C8DDE6' }}>
                    <strong style={{ color: '#EAF4F8' }}>Hyperbaric Chamber:</strong> Famagusta General Hospital
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <div
                    className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.25)' }}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="#f87171" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5" aria-hidden="true"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>
                  </div>
                  <p className="text-xs" style={{ color: '#C8DDE6' }}>
                    <strong style={{ color: '#EAF4F8' }}>Hyperbaric Chamber:</strong> Paphos General Hospital
                  </p>
                </div>
                <div
                  className="mt-3 pt-3"
                  style={{ borderTop: '1px solid rgba(239,68,68,0.15)' }}
                >
                  <p className="text-xs italic" style={{ color: 'rgba(200,221,230,0.7)' }}>
                    Always dive with a certified dive centre and follow safety protocols. This site provides information only and does not offer training, guiding, or supervision.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PlanYourDive;